import { useEffect, useState } from 'react';
import { Platform } from 'react-native';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt(): Promise<void>;
}

const STORAGE_KEY = 'pwa-banner-last-dismissed';
const SIX_HOURS = 6 * 60 * 60 * 1000;

const shouldShowBanner = (): boolean => {
  try {
    const lastDismissed = localStorage.getItem(STORAGE_KEY);
    if (!lastDismissed) return true;
    return Date.now() - Number(lastDismissed) >= SIX_HOURS;
  } catch {
    return true;
  }
};

const markBannerDismissed = () => {
  try {
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
  } catch {
    // localStorage unavailable
  }
};

export const usePwaInstall = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstallable, setIsInstallable] = useState(false);

  useEffect(() => {
    if (Platform.OS !== 'web') return;

    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      if (!shouldShowBanner()) return;
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setIsInstallable(true);
    };

    const handleAppInstalled = () => {
      // Hide the install banner immediately if installed via browser UI
      setIsInstallable(false);
      setDeferredPrompt(null);
      markBannerDismissed();
      console.log('PWA was installed');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstallable(false);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleCancel = () => {
    setDeferredPrompt(null);
    setIsInstallable(false);
    markBannerDismissed();
  };

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    // Show the browser installation prompt
    await deferredPrompt.prompt();

    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;
    console.log(`User response to install prompt: ${outcome}`);

    // We've used the prompt, it can't be used again. Clear it.
    handleCancel();
  };
  return { isInstallable, handleInstallClick, handleCancel };
};
